import React, { useState, useEffect, useRef } from 'react';
import Tabs from '../../../../WFTabs/Tabs';
import {
  PiList,
  PiLock,
  PiTag,
  PiBracketsCurly,
  PiCode,
} from 'react-icons/pi';
import { FaTrashAlt, FaRegCopy, FaPlus } from 'react-icons/fa';
import './styles.scoped.scss';
import CodeMirror from '@uiw/react-codemirror';
import { javascript } from '@codemirror/lang-javascript';
import { connect } from 'react-redux';
import clipboardCopy from 'clipboard-copy';
import { Link } from 'react-router-dom';
import {
  endpointForUrl,
  pathParamFromUrl,
  queryParamFromUrl,
} from '../../../../../services/dummy';
import { WEB_API_BASE_URL } from '../../../../../services/webApiAxios';
import { notification, ROUTES } from '../../../../../assets/constants/strings';

const mapStateToProps = (state) => ({
  user: state.user.user,
});

/**
 * Postman-style request details: Params | Authorization | Headers | Body | Code.
 * Params edits rewrite the URL (and vice versa); Authorization explains how the
 * playground signs requests; Code generates a copy-paste cURL example.
 */
const RequestBody = ({ handleBody, url, setUrl, user }) => {
  const [jsonInput, setJsonInput] = useState('');
  const [headers, setHeaders] = useState([]);
  const [apiKey, setApiKey] = useState('');

  useEffect(() => {
    handleBody({ jsonInput, headers, apiKey });
  }, [jsonInput, headers, apiKey]);

  // Keep the panel bound to the last resolved endpoint. Editing a path param can make the URL
  // transiently unresolvable (e.g. clearing the id → ".../items/"), and we don't want the whole
  // params form to vanish mid-edit — so fall back to the endpoint we last recognised.
  const resolvedEndpoint = endpointForUrl(url);
  const lastEndpointRef = useRef(null);
  if (resolvedEndpoint) lastEndpointRef.current = resolvedEndpoint;
  const endpoint = resolvedEndpoint || lastEndpointRef.current;

  // When a POST endpoint (the AI query) is selected, prefill the Body tab with its
  // example JSON so it's one click from a result; clear the body for GET endpoints.
  useEffect(() => {
    if (endpoint && endpoint.request === 'POST' && endpoint.body) {
      setJsonInput(JSON.stringify(endpoint.body, null, 2));
    } else {
      setJsonInput('');
    }
  }, [url]);

  const handleHeaderChange = (index, event) => {
    const newHeaders = headers.map((header, i) => {
      if (i === index) {
        return { ...header, [event.target.name]: event.target.value };
      }
      return header;
    });

    setHeaders(newHeaders);
  };

  const addHeader = () => {
    setHeaders([...headers, { key: '', value: '' }]);
  };

  const removeHeader = (index) => {
    const newHeaders = headers.filter((_, i) => i !== index);
    setHeaders(newHeaders);
  };

  const handleInputChange = React.useCallback((val, viewUpdate) => {
    setJsonInput(val);
  }, []);

  const copyText = (text, what) => {
    clipboardCopy(text)
      .then(() => notification('success', `${what} copied to clipboard.`))
      .catch(() => notification('info', `Could not copy the ${what.toLowerCase()}.`));
  };

  // ---- Params -------------------------------------------------------------
  // Rebuild the URL from the template, filling EVERY param with its current value
  // (the edited one with the new value, the rest read back from the live URL) so
  // multi-param endpoints don't reset their other fields. Path params are spliced
  // into the template; query params are URL-encoded.
  const paramValue = (param) =>
    param.in === 'query'
      ? queryParamFromUrl(url, param.name)
      : pathParamFromUrl(url, endpoint && endpoint.pathTemplate, param.name);

  const handleParamChange = (changedParam, changedValue) => {
    if (!endpoint) return;
    let next = endpoint.pathTemplate;
    endpoint.params.forEach((param) => {
      const value =
        param.name === changedParam.name ? changedValue : paramValue(param);
      const placeholder = `{${param.name}}`;
      const replacement =
        param.in === 'query' ? encodeURIComponent(value || '') : value || '';
      next = next.replace(placeholder, replacement);
    });
    setUrl(next);
  };

  const paramsPanel = (
    <div className='request-tab'>
      {endpoint && <p className='request-tab__description'>{endpoint.description}</p>}
      {endpoint && endpoint.params.length > 0 ? (
        <table className='params-table'>
          <thead>
            <tr>
              <th>Name</th>
              <th>Value</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            {endpoint.params.map((param) => (
              <tr key={param.name}>
                <td>
                  <code>{param.name}</code>
                  <span className='params-table__in'>{param.in}</span>
                </td>
                <td>
                  {param.options ? (
                    <select
                      className='form-control form-control-sm'
                      value={paramValue(param) || param.options[0]}
                      onChange={(e) => handleParamChange(param, e.target.value)}
                    >
                      {param.options.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type='text'
                      className='form-control form-control-sm'
                      value={paramValue(param)}
                      placeholder={param.example}
                      onChange={(e) => handleParamChange(param, e.target.value)}
                    />
                  )}
                </td>
                <td className='params-table__description'>
                  {param.description}{' '}
                  {param.required ? (
                    <span className='params-table__req'>required</span>
                  ) : (
                    <span className='params-table__opt'>optional</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className='request-tab__muted mb-0'>
          {endpoint && endpoint.request === 'POST'
            ? 'This endpoint takes a JSON body — see the Body tab, then press Send.'
            : endpoint
            ? 'This endpoint takes no parameters — press Send to run it.'
            : 'Pick an endpoint from the menu to see its parameters.'}
        </p>
      )}
    </div>
  );

  // ---- Authorization ------------------------------------------------------
  const sessionToken = (user && user.token) || '';
  const maskedToken = sessionToken
    ? `${sessionToken.slice(0, 16)}…${sessionToken.slice(-6)}`
    : '';

  const authPanel = (
    <div className='request-tab'>
      <p className='request-tab__label'>In the API Explorer</p>
      <p className='request-tab__description'>
        Requests are signed automatically with your signed-in session token —
        nothing to configure here:
      </p>
      <div className='auth-header-preview'>
        <code>Authorization: Bearer {maskedToken || '<your token>'}</code>
        {sessionToken && (
          <button
            className='rb-copy auth-header-preview__copy'
            type='button'
            title='Copy the full token'
            onClick={() => copyText(sessionToken, 'Token')}
          >
            <FaRegCopy />
          </button>
        )}
      </div>

      <hr />

      <p className='request-tab__label'>In your own app</p>
      <p className='request-tab__description'>
        Authenticate with an API key instead of the session token:
      </p>
      <ol className='auth-steps'>
        <li>
          Create a key on the <Link to={ROUTES.APIKeys}>API Keys</Link> page —
          you'll get a <b>key</b> and a <b>secret</b> (the secret is shown only
          once, so copy it then).
        </li>
        <li>Send both as headers with every request:</li>
      </ol>
      <div className='auth-header-preview'>
        <code>
          X-Api-Key: YOUR_API_KEY
          <br />
          X-Api-Secret: YOUR_API_SECRET
        </code>
      </div>
      <p className='request-tab__muted'>
        Your account needs an active Developer API subscription. See the{' '}
        <b>Code</b> tab for a ready-to-run example.
      </p>

      <hr />

      <label className='request-tab__label' htmlFor='playground-api-key'>
        Test with a specific key (optional)
      </label>
      <input
        id='playground-api-key'
        type='text'
        value={apiKey}
        className='form-control'
        onChange={(e) => setApiKey(e.target.value)}
        placeholder='Paste an API key to use it in the Code tab snippet'
      />
    </div>
  );

  // ---- Code snippet -------------------------------------------------------
  const fullUrl = `${WEB_API_BASE_URL}${(url || '').replace(/^\/api/, '')}`;
  const isPost = endpoint && endpoint.request === 'POST';
  const paramCount = (endpoint && endpoint.params.length) || 0;
  const hasBodyPayload = isPost && !!(jsonInput && jsonInput.trim());
  const curlLines = [
    `curl -X ${isPost ? 'POST' : 'GET'} "${fullUrl}" \\`,
    `  -H "X-Api-Key: ${apiKey || 'YOUR_API_KEY'}" \\`,
    `  -H "X-Api-Secret: YOUR_API_SECRET"${isPost ? ' \\' : ''}`,
  ];
  if (isPost) {
    curlLines.push('  -H "Content-Type: application/json" \\');
    curlLines.push(
      `  -d '${jsonInput || JSON.stringify(endpoint.body)}'`
    );
  }
  const curlSnippet = curlLines.join('\n');

  const codePanel = (
    <div className='request-tab'>
      <p className='request-tab__description'>
        The same request from your terminal or code — authenticate with your API
        key and secret:
      </p>
      <div className='code-snippet'>
        <pre className='mb-0'>{curlSnippet}</pre>
        <button
          className='rb-copy rb-copy--dark code-snippet__copy'
          type='button'
          title='Copy the cURL command'
          onClick={() => copyText(curlSnippet, 'cURL command')}
        >
          <FaRegCopy />
        </button>
      </div>
    </div>
  );

  const tabs = [
    {
      title: (
        <>
          Params
          {paramCount > 0 && <span className='rb-count'>{paramCount}</span>}
        </>
      ),
      id: 0,
      icon: <PiList />,
    },
    {
      title: 'Authorization',
      id: 1,
      icon: <PiLock />,
    },
    {
      title: (
        <>
          Headers
          {headers.length > 0 && (
            <span className='rb-count'>{headers.length}</span>
          )}
        </>
      ),
      id: 2,
      icon: <PiTag />,
    },
    {
      title: (
        <>
          Body
          {hasBodyPayload && (
            <span className='rb-dot' title='JSON body loaded' />
          )}
        </>
      ),
      id: 3,
      icon: <PiBracketsCurly />,
    },
    {
      title: 'Code',
      id: 4,
      icon: <PiCode />,
    },
  ];

  const tabPanels = [
    {
      content: paramsPanel,
      id: 0,
    },
    {
      content: authPanel,
      id: 1,
    },
    {
      content: (
        <div className='request-tab'>
          {headers.length === 0 && (
            <p className='request-tab__muted mb-2'>
              No custom headers. Auth is handled for you — add a header only if
              you need one.
            </p>
          )}
          {headers.map((header, index) => (
            <div key={index} className='rb-header-row'>
              <input
                type='text'
                className='form-control'
                name='key'
                value={header.key}
                onChange={(e) => handleHeaderChange(index, e)}
                placeholder='Key'
              />
              <input
                type='text'
                name='value'
                className='form-control'
                value={header.value}
                onChange={(e) => handleHeaderChange(index, e)}
                placeholder='Value'
              />
              <button
                type='button'
                className='rb-icon-btn'
                title='Remove header'
                onClick={() => removeHeader(index)}
              >
                <FaTrashAlt />
              </button>
            </div>
          ))}
          <button type='button' className='rb-btn rb-btn--add' onClick={addHeader}>
            <FaPlus /> Add header
          </button>
        </div>
      ),
      id: 2,
    },
    {
      content: (
        <div className='request-tab'>
          {isPost ? (
            <p className='request-tab__description'>
              Provide the request body as JSON — a <code>query</code> (your
              question) and a <code>documentIds</code> array (at least one).
            </p>
          ) : (
            <p className='request-tab__muted'>
              Most endpoints are GET and don't require a body. The AI query
              (Query a Document) takes a JSON body.
            </p>
          )}
          <CodeMirror
            value={jsonInput}
            height='120px'
            extensions={[javascript({ jsx: true })]}
            onChange={handleInputChange}
          />
        </div>
      ),
      id: 3,
    },
    {
      content: codePanel,
      id: 4,
    },
  ];

  return (
    <div className='wf-request-body px-3'>
      <Tabs tabs={tabs} tabPanels={tabPanels} />
    </div>
  );
};

export default connect(mapStateToProps, null)(RequestBody);
